/**
 * IntakeTable Component
 * Displays food items in a table with edit/delete actions.
 * In edit mode, rows are sortable via drag-and-drop (@dnd-kit/sortable).
 * Inactive alternative rows (aktivne=false) are visually dimmed, show a toggle button,
 * and now always display their nutritional values.
 * Multi‑select checkboxes and bulk delete are available in edit mode.
 */

import { useCallback, useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Edit, Trash2, GripVertical, ToggleLeft } from "lucide-react";
import { calculateNutrition } from "../utils";
import { useTranslation } from "@/hooks/useTranslation";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import type { Prijem, PrijemKategoria } from "../types";
import { FpcAlternativesPanel } from "./FpcAlternativesPanel";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IntakeTableProps {
  items: Prijem[];
  isEditMode: boolean;
  editingItem: Prijem | null;
  /** If omitted, edit action is hidden (read-only mode). */
  onEdit?: (item: Prijem) => void;
  /** If omitted, delete action is hidden (read-only mode). */
  onDelete?: (id: number) => Promise<void>;
  /** If omitted, inline update is disabled (read-only mode). */
  onUpdate?: (id: number, hmotnost: string, poznamka: string) => Promise<void>;
  onCancelEdit: () => void;
  setEditingItem: (item: Prijem | null) => void;
  /** Called when the user finishes dragging to a new position within this category */
  onReorder?: (orderedIds: number[]) => void;
  /** Called when the user selects a different meal category from the move dropdown */
  onMoveToKategoria?: (prijemId: number, newKategoriaId: number) => void;
  /** All available meal categories (for the move dropdown) */
  allKategorie?: PrijemKategoria[];
  /** The current category id (to exclude from move options) */
  currentKategoriaId?: number | null;
  /** klientId for pantry availability in FPC alternatives panel */
  klientId?: number | null;
}

// ---------------------------------------------------------------------------
// SortableRow
// ---------------------------------------------------------------------------

interface SortableRowProps {
  item: Prijem;
  isEditMode: boolean;
  isEditing: boolean;
  editingItem: Prijem | null;
  onEdit?: (item: Prijem) => void;
  onDelete?: (id: number) => Promise<void>;
  onUpdate?: (id: number, hmotnost: string, poznamka: string) => Promise<void>;
  onCancelEdit: () => void;
  setEditingItem: (item: Prijem | null) => void;
  onMoveToKategoria?: (prijemId: number, newKategoriaId: number) => void;
  allKategorie?: PrijemKategoria[];
  currentKategoriaId?: number | null;
  klientId?: number | null;
  /** Multi‑select props */
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
}

function SortableRow({
  item,
  isEditMode,
  isEditing,
  editingItem,
  onEdit,
  onDelete,
  onUpdate,
  onCancelEdit,
  setEditingItem,
  onMoveToKategoria,
  allKategorie = [],
  currentKategoriaId,
  klientId,
  selectedIds,
  onToggleSelect,
}: SortableRowProps) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  // A row is an "inactive alternative" when it has a parent link AND is not active.
  // Once toggled to active (aktivne=true, alternativaPre=null) it is the new original.
  // The old original after toggle has alternativaPre set and aktivne=false — show label.
  const isAlternative =
    item.alternativaPre !== null && item.alternativaPre !== undefined;
  const isInactive = item.aktivne === false;
  // Show the "(alternatíva)" badge only when the row is currently inactive
  const showAlternativeLabel = isInactive && isAlternative;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    // Alternative rows can be dragged
    //disabled: isAlternative,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Nutrition always calculated, even for inactive alternatives (so they display values).
  const nutrition = calculateNutrition(
    item.potravina,
    isEditing && editingItem ? editingItem.hmotnost : item.hmotnost
  );

  // Toggle alternative mutation
  const toggleMutation = trpc.prijem.toggleAlternative.useMutation({
    onSuccess: () => {
      toast.success("Alternatíva aktivovaná");
      utils.prijem.getByKlientAndDate.invalidate();
      utils.prijem.getDailyTotals.invalidate();
    },
    onError: err => {
      toast.error(`Chyba: ${err.message}`);
    },
  });

  // Other categories the item can be moved to (only for non-alternatives)
  const moveTargets = isAlternative
    ? []
    : allKategorie.filter(k => k.id !== currentKategoriaId);

  // Row styling for inactive alternatives
  const rowClassName = isInactive
    ? "opacity-50 bg-muted/30 border-l-2 border-l-amber-400"
    : "";

  if (isEditing && editingItem) {
    return (
      <TableRow
        ref={setNodeRef}
        style={style}
        className={`bg-muted/50 ${rowClassName}`}
      >
        {isEditMode && (
          <TableCell className="w-8 px-1">
            {/* No drag handle while editing */}
          </TableCell>
        )}
        <TableCell className="font-medium">
          {item.potravina.nazov}
          {showAlternativeLabel && (
            <span className="ml-2 text-xs text-amber-600 font-normal">
              (alternatíva)
            </span>
          )}
        </TableCell>
        <TableCell>
          <Input
            type="number"
            value={editingItem.hmotnost ?? "100"}
            onChange={e =>
              setEditingItem({ ...editingItem, hmotnost: e.target.value })
            }
            className="w-24"
            step="0.1"
            min="0"
          />
        </TableCell>
        <TableCell className="text-right">{nutrition.energiaKcal}</TableCell>
        <TableCell className="text-right">{nutrition.proteinG}</TableCell>
        <TableCell className="text-right">{nutrition.tukG}</TableCell>
        <TableCell className="text-right">{nutrition.sacharidG}</TableCell>
        <TableCell>
          <Textarea
            value={editingItem.poznamka ?? ""}
            onChange={e =>
              setEditingItem({ ...editingItem, poznamka: e.target.value })
            }
            className="min-h-[60px] text-sm"
          />
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                onUpdate?.(
                  item.id,
                  editingItem.hmotnost != null ? editingItem.hmotnost : "0",
                  editingItem.poznamka ?? ""
                )
              }
            >
              {t("table.save", "Save")}
            </Button>
            <Button variant="outline" size="sm" onClick={onCancelEdit}>
              {t("dialog.cancel", "Cancel")}
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow ref={setNodeRef} style={style} className={rowClassName}>
      {/* Checkbox column – visible only in edit mode */}
      {isEditMode && (
        <TableCell className="w-8 px-1">
          <input
            type="checkbox"
            checked={selectedIds.has(item.id)}
            onChange={() => onToggleSelect(item.id)}
            className="cursor-pointer"
          />
        </TableCell>
      )}

      {/* Drag handle column – now always visible in edit mode */}
      {isEditMode && (
        <TableCell className="w-8 px-1 cursor-grab active:cursor-grabbing text-muted-foreground">
          <span {...attributes} {...listeners} className="inline-flex">
            <GripVertical className="h-4 w-4" />
          </span>
        </TableCell>
      )}
      <TableCell className="font-medium">
        {item.potravina.nazov}
        {showAlternativeLabel && (
          <span className="ml-2 text-xs text-amber-600 font-normal">
            (alternatíva)
          </span>
        )}
        {isInactive && (
          <span className="ml-1 text-xs text-muted-foreground">
            [neaktívna]
          </span>
        )}
      </TableCell>
      <TableCell>
        {item.hmotnost != null && item.hmotnost !== ""
          ? item.hmotnost
          : item.potravina.balenieG || 100}
      </TableCell>
      {/* Inactive alternatives show dashes for nutrition columns */}
      <TableCell className="text-right">
        {isInactive ? (
          <span className="text-muted-foreground text-xs">
            {nutrition.energiaKcal}
          </span>
        ) : (
          <span className="text-amber-600">{nutrition.energiaKcal}</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {isInactive ? (
          <span className="text-muted-foreground text-xs">
            {nutrition.proteinG}
          </span>
        ) : (
          <span className="text-blue-600">{nutrition.proteinG}</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {isInactive ? (
          <span className="text-muted-foreground text-xs">
            {nutrition.tukG}
          </span>
        ) : (
          <span className="text-orange-600">{nutrition.tukG}</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {isInactive ? (
          <span className="text-muted-foreground text-xs">
            {nutrition.sacharidG}
          </span>
        ) : (
          <span className="text-teal-600">{nutrition.sacharidG}</span>
        )}
      </TableCell>
      <TableCell className="text-right text-sm text-muted-foreground">
        {item.poznamka || "-"}
      </TableCell>
      {isEditMode && (
        <TableCell className="text-right">
          <div className="flex justify-end items-center gap-1">
            {/* Toggle button: only for inactive alternatives */}
            {isAlternative && isInactive && (
              <Button
                variant="ghost"
                size="sm"
                title="Aktivovať túto alternatívu (deaktivuje originál)"
                className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700"
                disabled={toggleMutation.isPending}
                onClick={() =>
                  toggleMutation.mutate({ alternativeId: item.id })
                }
              >
                <ToggleLeft className="h-4 w-4" />
              </Button>
            )}

            {/* FPC alternatives panel — shown for any row with an fpcKod */}
            {item.potravina.fpcKod && (
              <FpcAlternativesPanel item={item} klientId={klientId} />
            )}

            {/* Move to another category — only for non-alternatives */}
            {!isAlternative && moveTargets.length > 0 && onMoveToKategoria && (
              <select
                className="text-xs border rounded px-1 py-0.5 bg-background text-foreground cursor-pointer max-w-[120px]"
                defaultValue=""
                onChange={e => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val)) {
                    onMoveToKategoria(item.id, val);
                    e.target.value = "";
                  }
                }}
                title={t("intake:table.moveTo", "Move to…")}
              >
                <option value="" disabled>
                  {t("intake:table.moveTo", "Move to…")}
                </option>
                {moveTargets.map(k => (
                  <option key={k.id} value={k.id}>
                    {k.name ?? k.nazov ?? String(k.id)}
                  </option>
                ))}
              </select>
            )}

            {onEdit && (
              <Button variant="ghost" size="sm" onClick={() => onEdit(item)}>
                <Edit className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(item.id)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// IntakeTable
// ---------------------------------------------------------------------------

export function IntakeTable({
  items,
  isEditMode,
  editingItem,
  onEdit,
  onDelete,
  onUpdate,
  onCancelEdit,
  setEditingItem,
  onReorder,
  onMoveToKategoria,
  allKategorie,
  currentKategoriaId,
  klientId,
}: IntakeTableProps) {
  const { t } = useTranslation();

  // Multi‑select state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  // Reset selection when the list changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [items]);

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (prev.size === items.length) {
        return new Set(); // deselect all
      }
      return new Set(items.map(i => i.id));
    });
  }, [items]);

  const handleDeleteSelected = useCallback(async () => {
    if (!onDelete || selectedIds.size === 0) return;
    setDeleting(true);
    try {
      // Delete each selected item sequentially
      for (const id of Array.from(selectedIds)) {
        await onDelete(id);
      }
      setSelectedIds(new Set());
    } finally {
      setDeleting(false);
    }
  }, [onDelete, selectedIds]);

  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex(i => i.id === active.id);
      const newIndex = items.findIndex(i => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(items, oldIndex, newIndex);
      onReorder?.(reordered.map(i => i.id));
    },
    [items, onReorder]
  );

  const ids = items.map(i => i.id);

  return (
    <div>
      {/* Bulk delete button */}
      {isEditMode && someSelected && (
        <div className="mb-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteSelected}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : `Delete Selected (${selectedIds.size})`}
          </Button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* Checkbox column header (select all) */}
                  {isEditMode && (
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        className="cursor-pointer"
                      />
                    </TableHead>
                  )}
                  {isEditMode && <TableHead className="w-8" />}
                  <TableHead>{t("intake:table.food", "Food")}</TableHead>
                  <TableHead>
                    {t("intake:table.amount", "Amount (g)")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("intake:table.energy", "Energy (kcal)")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("intake:table.protein", "Protein (g)")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("intake:table.fat", "Fat (g)")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("intake:table.carbs", "Carbs (g)")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("intake:table.notes", "Notes")}
                  </TableHead>
                  {isEditMode && (
                    <TableHead className="text-right">
                      {t("intake:table.actions", "Actions")}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(item => (
                  <SortableRow
                    key={item.id}
                    item={item}
                    isEditMode={isEditMode}
                    isEditing={editingItem?.id === item.id}
                    editingItem={editingItem}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onUpdate={onUpdate}
                    onCancelEdit={onCancelEdit}
                    setEditingItem={setEditingItem}
                    onMoveToKategoria={onMoveToKategoria}
                    allKategorie={allKategorie}
                    currentKategoriaId={currentKategoriaId}
                    klientId={klientId}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
